import type { CvChange, GeneratedCv } from '../types';

/**
 * Une modification ne s'applique que si la puce (ou le titre) visé est toujours celui que le serveur
 * a lu (`before`) : une désynchronisation ne doit jamais écraser un autre texte.
 */
export function modificationApplicable(cv: GeneratedCv, c: CvChange): boolean {
  if (c.target === 'headline') return cv.headline === c.before;
  if (c.item === undefined || c.bullet === undefined) return false;
  if (c.section !== 'experiences' && c.section !== 'projects') return false;
  return cv[c.section][c.item]?.bullets[c.bullet] === c.before;
}

/** Combien de modifications retenues s'appliqueront vraiment. */
export function nombreAppliquees(cv: GeneratedCv, changes: CvChange[], retenues: ReadonlySet<string>): number {
  return changes.filter((c) => retenues.has(c.id) && modificationApplicable(cv, c)).length;
}

/**
 * Applique au CV les modifications retenues et RIEN d'autre. Fonction pure : le CV d'entrée n'est
 * pas modifié. Le CV rendu n'a pas de score : `score_inconnu` le dit, `ats_score` n'est qu'un
 * remplissage.
 */
export function appliquerModifications(cv: GeneratedCv, changes: CvChange[], retenues: ReadonlySet<string>): GeneratedCv {
  const experiences = cv.experiences.map((e) => ({ ...e, bullets: [...e.bullets] }));
  const projects = cv.projects.map((p) => ({ ...p, bullets: [...p.bullets] }));
  let headline = cv.headline;

  for (const c of changes) {
    if (!retenues.has(c.id) || !modificationApplicable(cv, c)) continue;
    if (c.target === 'headline') {
      headline = c.after;
    } else {
      const liste = c.section === 'projects' ? projects : experiences;
      liste[c.item as number].bullets[c.bullet as number] = c.after;
    }
  }

  return { ...cv, headline, experiences, projects, ats_score: 0, advice: [], score_inconnu: true };
}

/**
 * Où se trouve la modification, en mots : « Stagiaire · ACME » ou « Mon projet ». null pour le titre
 * ou quand l'élément n'existe plus.
 */
export function lieuModification(cv: GeneratedCv, c: CvChange): string | null {
  if (c.target !== 'bullet' || c.item === undefined) return null;
  if (c.section === 'projects') return cv.projects[c.item]?.name?.trim() || null;
  const e = cv.experiences[c.item];
  if (!e) return null;
  return [e.role, e.company].map((x) => x?.trim()).filter(Boolean).join(' · ') || null;
}
